import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { SystemApp } from '../../constans/SystemConfigurations';
import { TokenStore } from './token-store';

/**
 * Owns the JWT access-token lifecycle for the admin panel (Token -> JWT
 * migration). Mirrors font-end's AuthSessionService (services/auth-session.service.ts)
 * - see that file's history for the full incident this design exists to not
 * repeat:
 *
 * - `withCredentials: true` is set here AND unconditionally by
 *   AuthInterceptor for every request in this app, not gated by URL. A login
 *   response is the first thing that tries to set the `sale4all_refresh`
 *   cookie, and it does not live under `/api/auth/` (it's `/api-token-auth/`)
 *   - a URL allowlist is always one endpoint away from missing that, which
 *   is exactly what happened on font-end.
 * - The CSRF cookie is seeded directly on the login response now
 *   (accounts/views.py, CustomObtainAuthToken.post), so ensureCsrf() here is
 *   a fallback for a page that reloads before ever logging in again, not the
 *   only way this app obtains one.
 */
@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly refreshUrl = `${SystemApp.base_url_api}/api/auth/token/refresh/`;
  private readonly logoutUrl = `${SystemApp.base_url_api}/api/auth/logout/`;
  private readonly csrfUrl = `${SystemApp.base_url_api}/api/auth/csrf/`;

  /**
   * The in-flight refresh, if any. SINGLE-FLIGHT, and load-bearing: several
   * requests routinely 401 together when the access token expires. Because
   * ROTATE_REFRESH_TOKENS is on, a second concurrent refresh would present an
   * already-rotated token, the backend would read that as reuse, and chain
   * revocation would sign the admin out entirely. One shared refresh turns a
   * routine expiry into a non-event.
   */
  private inFlight$: Observable<string | null> | null = null;

  constructor(private http: HttpClient) {}

  private hasCsrfCookie(): boolean {
    if (typeof document === 'undefined' || !document.cookie) {
      return false;
    }
    return document.cookie.split(';').some(c => c.trim().startsWith('csrftoken='));
  }

  /** Fetch the CSRF cookie if we do not already have one. Always resolves -
   * a failure here must not block anything, the call that actually needs the
   * token will fail on its own and be handled there. */
  ensureCsrf(): Promise<void> {
    if (this.hasCsrfCookie()) {
      return Promise.resolve();
    }
    return this.http
      .get(this.csrfUrl, { withCredentials: true })
      .toPromise()
      .then(() => undefined)
      .catch(() => undefined);
  }

  /**
   * POST to the refresh endpoint, one browser tab at a time.
   *
   * Every tab (and the admin panel) shares ONE HttpOnly refresh cookie, and
   * rotation means each refresh replaces it. When the browser is reopened all
   * restored tabs refresh at the same instant with the same cookie, and the
   * losers used to look like token theft to the server. A Web Lock makes
   * same-origin tabs queue: the next tab only sends its request after the
   * previous one has stored the new cookie. Browsers without Web Locks fall
   * back to a plain request (the server also tolerates a short overlap).
   */
  private lockedRefreshPost(): Observable<any> {
    const post = () => this.http
      .post<any>(this.refreshUrl, {}, { withCredentials: true })
      .toPromise();
    const locks = typeof navigator !== 'undefined' ? (navigator as any).locks : null;
    if (locks && typeof locks.request === 'function') {
      return from(locks.request('sale4all-token-refresh', post) as Promise<any>);
    }
    return from(post());
  }

  refresh(): Observable<string | null> {
    if (this.inFlight$) {
      return this.inFlight$;
    }

    this.inFlight$ = this.lockedRefreshPost()
      .pipe(
        map(res => (res && res.access) || null),
        tap(access => {
          TokenStore.set(access);
          this.inFlight$ = null;
        }),
        catchError(err => {
          this.inFlight$ = null;
          TokenStore.clear();
          // TOKEN_REUSE_DETECTED means the session was revoked for security
          // reasons and is not retryable - surfaced rather than swallowed.
          const code = err && err.error && err.error.statusCode;
          if (code === 'TOKEN_REUSE_DETECTED') {
            return throwError(err);
          }
          return of(null);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.inFlight$;
  }

  /** Called once at bootstrap via APP_INITIALIZER (auth.provider.ts). The
   * access token never survives a reload, so without this an admin who has a
   * JWT session falls back to whatever legacy token they still have (or
   * appears signed out) on every refresh instead of it being silently
   * restored. Always resolves - must not block the app from starting. */
  restoreSession(): Promise<void> {
    if (!TokenStore.isLoggedIn()) {
      return Promise.resolve();
    }
    return this.ensureCsrf()
      .then(() => this.refresh().toPromise())
      .then(() => undefined)
      .catch(() => undefined);
  }

  /**
   * Revoke the refresh token server-side, then clear local JWT state.
   * Best-effort and non-blocking for the caller (UsersService.signOut() is
   * synchronous) - but this is the one place a stolen admin refresh token
   * actually gets revoked instead of staying valid for its full 60-day
   * lifetime, so it is still worth doing even though nothing awaits it.
   */
  async logout(): Promise<void> {
    try {
      await this.ensureCsrf();
      await this.http.post(this.logoutUrl, {}, { withCredentials: true }).toPromise();
    } catch (e) {
      // Offline, or already revoked - clear local state below regardless.
    }
    TokenStore.clear();
  }
}
